import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Injector,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  forwardRef,
  Output,
  EventEmitter,
  Optional,
  Self,
  ContentChild,
  TemplateRef,
  ViewContainerRef,
  HostListener,
  AfterViewChecked,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject, takeUntil, tap } from 'rxjs';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { MatChipGrid } from '@angular/material/chips';
import { MatDialogRef } from '@angular/material/dialog';
import { AgVirtualSrollComponent } from 'ag-virtual-scroll';
import _ from 'lodash';
import { ISvcChipOption } from '../interfaces/svc-chip.interface';
import { SvcControl } from '../../svc-control';
import { SvcMediaQuery, SvcTestIdDirective } from 'projects/lib-shared-common/src/public-api';
import { optionsContainer } from '../../utils/droplists.animations';
import { MAX_OPTIONS_LIST_HEIGHT } from '../../utils/droplists.const';

@Component({
  selector: 'svc-chip',
  templateUrl: './svc-chip.component.html',
  styleUrls: ['./svc-chip.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SvcChipComponent),
      multi: true,
    },
  ],
  animations: [optionsContainer],
  host: {
    '[class.svc-field-in-primary]': 'inPrimaryAppearance',
  },
})
export class SvcChipComponent extends SvcControl implements OnInit, AfterViewChecked, OnChanges, OnDestroy {
  protected inPrimaryAppearance = false;

  _options: ISvcChipOption[];

  public filteredOptions: ISvcChipOption[] = [];
  public selectedOptions: ISvcChipOption[] = [];
  public canShowSearchInput: boolean;
  public maxOptionsListHeight = MAX_OPTIONS_LIST_HEIGHT;
  public collapsed = true;

  @Optional() @ContentChild(TemplateRef) templateRef: TemplateRef<any>;
  @ViewChild('formField', { read: ElementRef }) formField: ElementRef<HTMLElement>;
  @ViewChild('optionsListTemplate') optionsListTemplate: TemplateRef<any>;
  @ViewChild('readonlyOptionsContainer') readonlyOptionsContainer: ElementRef<HTMLElement>;
  @ViewChild(MatChipGrid) matChipGrid: MatChipGrid;
  @ViewChild(AgVirtualSrollComponent) agVirtualScroll: AgVirtualSrollComponent;

  @Input() label: string | { text: string; icon: string };
  @Input() icon: string;
  @Input() readonly: boolean = false;
  @Input() showValidation: boolean = true;
  @Input() readonlyStyle: 'default' | 'chip' | 'transparent-chip' = 'default';
  @Input() set options(value: ISvcChipOption[]) {
    const options = [...(value ? value : [])];
    this._options = _.cloneDeep(options);
    this._checkCanShowSearchInput();
  }
  @Input() type: 'single' | 'multiple' = 'multiple';
  @Input() appearance: 'default' | 'primary' = 'default';
  @Input() inputId: string;
  @Input() placeholder: string;
  @Input() fallbackTextValue: string;
  @Input() truncateTextValue: boolean = false;
  @Input() collapsedDisplaying: boolean = true;
  @Input() loading: boolean = false;

  @Output() onSelectChanged = new EventEmitter<{ option: ISvcChipOption; selected: boolean }>();

  protected valueText: string;
  protected searchText: string = null;
  protected optionsListIsOpened = false;
  protected undisplayedSelectedOptions = 0;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  private _isUpdatingControl = false;
  private _overlayRef: OverlayRef;
  private _initialDisableClose = false;
  private _overlayScrollCleanup?: () => void;
  private _scrollCdScheduled = false;

  constructor(
    @Inject(Injector) injector: Injector,
    @Optional() private dialogRef: MatDialogRef<any>,
    private _viewContainerRef: ViewContainerRef,
    private _overlay: Overlay,
    private _mediaQuery: SvcMediaQuery,
    private _cdr: ChangeDetectorRef,
    private _ngZone: NgZone,
    @Optional() @Self() private readonly _svcTestId?: SvcTestIdDirective
  ) {
    super(injector);
    if (this._svcTestId) this._svcTestId.useOnHost = false;
  }

  public get inputTestId(): string | null {
    return this._svcTestId?.normalizedTestId || null;
  }

  private _setupOverlayScrollFix(): void {
    this._overlayScrollCleanup?.();

    const overlayRoot = this._overlayRef?.overlayElement;
    if (!overlayRoot) return;

    const targets = new Set<HTMLElement>();
    const agVirtualScrollEl = overlayRoot.querySelector('ag-virtual-scroll') as HTMLElement | null;
    if (agVirtualScrollEl) targets.add(agVirtualScrollEl);

    const overflowAutoEl = overlayRoot.querySelector('.overflow-y-auto') as HTMLElement | null;
    if (overflowAutoEl) targets.add(overflowAutoEl);

    if (!targets.size) targets.add(overlayRoot);

    const onScroll = () => {
      if (this._scrollCdScheduled) return;
      this._scrollCdScheduled = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this._scrollCdScheduled = false;
          if (!this.optionsListIsOpened) return;
          try {
            this._ngZone.run(() => {
              this.agVirtualScroll?.refreshData?.();
              this._cdr.detectChanges();
            });
          } catch {}
        });
      });
    };

    const cleanups: Array<() => void> = [];
    targets.forEach((el) => {
      el.addEventListener('scroll', onScroll, { passive: true });
      cleanups.push(() => el.removeEventListener('scroll', onScroll));
    });

    this._overlayScrollCleanup = () => {
      cleanups.forEach((fn) => fn());
      this._scrollCdScheduled = false;
    };
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.collapsed = this.collapsedDisplaying ?? true;
    this._initialDisableClose = this.dialogRef?.disableClose ?? false;

    this._mediaQuery.size$
      .pipe(
        takeUntil(this._unsubscribeAll),
        tap(() => this._checkCanShowSearchInput())
      )
      .subscribe();

    this.control?.valueChanges
      .pipe(
        takeUntil(this._unsubscribeAll),
        tap((values: (string | number)[]) => {
          if (!this._isUpdatingControl) {
            if (!this.loading) {
              const selectedOptions = [];
              for (const option of this._options) {
                option.selected = Array.isArray(values) ? (values?.includes(option.value) ?? false) : values === option.value;
                if (option.selected) {
                  selectedOptions.push(option);
                }
              }
              this.selectedOptions = selectedOptions;
              this._checkCanShowSearchInput();
            }
            this.searchText = '';
            this._defineValueText();
            this.filterOptions(this.searchText);
          }
        })
      )
      .subscribe();

    this._checkSelectedOptions();
    this._defineValueText();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.control) {
      super.ngOnChanges(changes);
      this._checkSelectedOptions();
      this._update();
    }
    this.filterOptions(this.searchText);
    this._checkCanShowSearchInput();
    if ('appearance' in changes) {
      this.inPrimaryAppearance = this.appearance === 'primary';
    }
  }

  ngAfterViewChecked(): void {
    this._checkUndisplayedSelectedOptions();
  }

  private _checkCanShowSearchInput() {
    const size = this._mediaQuery.currentSize;
    this.canShowSearchInput = (!this.selectedOptions?.length || this.type === 'multiple') && ((this._options?.length > 100 && size.isXS) || size.isAboveXS);
  }

  private _checkUndisplayedSelectedOptions() {
    if (this.collapsed && !this.loading) {
      this.undisplayedSelectedOptions = 0;
      if (this.selectedOptions?.length > 1) {
        if (this.matChipGrid) {
          const length = this.matChipGrid?._chips.length ?? 0;
          for (let i = 0; i < length; i++) {
            const chip = this.matChipGrid?._chips.get(i);
            if (chip._elementRef.nativeElement.parentElement.offsetTop > 0) {
              this.undisplayedSelectedOptions = length - i;
              break;
            }
          }
        } else if (this.readonlyOptionsContainer?.nativeElement) {
          const containerEl = this.readonlyOptionsContainer?.nativeElement;
          const length = containerEl?.children.length ?? 0;
          for (let i = 0; i < length; i++) {
            const chipEl = containerEl.children[i] as HTMLElement;
            if (chipEl?.offsetTop > 0) {
              this.undisplayedSelectedOptions = length - i;
              break;
            }
          }
        }
      }
    }
  }

  private _defineValueText() {
    if (this.selectedOptions?.length > 0) {
      this.valueText = this.selectedOptions.map((o) => o.label).join(', ');
    } else {
      this.valueText = '';
    }
  }

  public filterOptions(value: string): void {
    const filterValue = value ? value.toString().toLowerCase() : '';
    this.filteredOptions = filterValue ? this._options.filter((option) => option.label.toString().toLowerCase().includes(filterValue)) : this._options;
  }

  private _available(value: any) {
    const item = this._options.find((option) => option.value.toString().toLowerCase() === value.toString().toLowerCase());
    if (item) {
      item.selected = false;
      this._update();
      this.onSelectChanged.emit({ option: item, selected: false });
      return true;
    }
    return false;
  }

  private _unavailable(value: any) {
    const item = this._options.find((option) => option.value.toString().toLowerCase() === value.toString().toLowerCase());
    if (item) {
      item.selected = true;
      this.selectedOptions.push(item);
      this._update();
      this.onSelectChanged.emit({ option: item, selected: true });
      return true;
    }
    return false;
  }

  private _update() {
    if (this.loading) return;
    const newValue = this.selectedOptions.map((option) => (option.selected ? option.value : null)).filter((option) => option != null);
    if (JSON.stringify(newValue) != JSON.stringify(this.control?.value ?? [])) {
      this._isUpdatingControl = true;
      this.control?.setValue(newValue);
      this._isUpdatingControl = false;
    }
    this._defineValueText();
  }

  private _checkSelectedOptions(): void {
    if (this.loading) return;

    const values = Array.isArray(this.control?.value) ? this.control.value : [];
    let selectedItems = [...(this.selectedOptions ?? [])].filter((item) => {
      return values.some((v) => v === item.value) && this._options.some((opt) => opt.value === item.value);
    });

    this._options.forEach((option) => {
      if (values.some((x) => x == option.value)) {
        const currentOptionIndex = selectedItems.findIndex((item) => item.value === option.value);
        option.selected = true;
        if (currentOptionIndex >= 0) {
          selectedItems[currentOptionIndex] = option;
        } else {
          if (this.type == 'single') {
            if (selectedItems.length == 0) {
              selectedItems.push(option);
            }
          } else {
            selectedItems.push(option);
          }
        }
      } else {
        selectedItems = selectedItems.filter((item) => item.value !== option.value);
      }
    });

    this.selectedOptions = selectedItems;
    this._checkCanShowSearchInput();
  }

  openOptions() {
    if (this.loading || this.optionsListIsOpened || (this.control?.disabled ?? false)) {
      return;
    }

    const hasOverlayRefInstance = !!this._overlayRef;

    if (!this._overlayRef && this.formField.nativeElement) {
      this._overlayRef = this._overlay.create({
        hasBackdrop: true,
        backdropClass: 'bg-transparent',
        scrollStrategy: this._overlay.scrollStrategies.block(),
        width: this.formField.nativeElement.clientWidth,
        maxHeight: MAX_OPTIONS_LIST_HEIGHT,
        positionStrategy: this._getOptionsPositionStrategy(),
      });

      this._overlayRef
        .backdropClick()
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(() => {
          this.closeOptions();
        });

      this._overlayRef
        .detachments()
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(() => {
          this.formField?.nativeElement?.focus();
          if (this.dialogRef) this.dialogRef.disableClose = this._initialDisableClose;
          setTimeout(() => {
            this.optionsListIsOpened = false;
            this._cdr.markForCheck();
          });
        });
    }

    if (hasOverlayRefInstance) {
      this._overlayRef?.updateSize({
        width: this.formField?.nativeElement?.clientWidth,
      });
      this._overlayRef.updatePositionStrategy(this._getOptionsPositionStrategy());
    }

    if (!this.optionsListIsOpened) {
      try {
        const viewRef = this._overlayRef.attach(new TemplatePortal(this.optionsListTemplate, this._viewContainerRef));
        this.optionsListIsOpened = true;
        this._setupOverlayScrollFix();
        if (this.dialogRef) this.dialogRef.disableClose = true;
        setTimeout(() => {
          if (this.optionsListIsOpened) {
            if (viewRef.rootNodes.length) {
              const element = viewRef.rootNodes[0] as HTMLElement;
              if (element?.querySelector) {
                const input: HTMLInputElement = element.querySelector('#app-svc-chip-search-input-options');
                if (input) {
                  input?.focus();
                } else {
                  this.formField?.nativeElement?.focus();
                }
              }
            }
          }
        });
      } catch {}
    }
  }

  closeOptions(options?: { fromOnDestroy?: boolean }) {
    const isOpened = this._overlayRef?.hasAttached() ?? false;
    this._overlayRef?.detach();
    this._overlayScrollCleanup?.();
    isOpened && !(options?.fromOnDestroy ?? false) && this.control?.markAsTouched();
  }

  private _getOptionsPositionStrategy() {
    const fieldWrapper = this.formField.nativeElement.querySelector('.mat-mdc-text-field-wrapper') as HTMLElement;
    return this._overlay
      .position()
      .flexibleConnectedTo(this.formField.nativeElement)
      .withLockedPosition(true)
      .withPush(true)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
          offsetY: fieldWrapper ? this.formField.nativeElement.clientHeight - fieldWrapper.offsetHeight : 0,
        },
      ]);
  }

  selected(option: ISvcChipOption, event?: MouseEvent): void {
    event?.stopImmediatePropagation();

    if (!option.selected && this.type === 'single') {
      this.closeOptions();
    }

    if (option.selected) {
      const index = this.selectedOptions.indexOf(option);
      if (index >= 0) {
        this.selectedOptions = [...this.selectedOptions.slice(0, index), ...this.selectedOptions.slice(index + 1)];
        option.selected = false;
        this._available(option.value);
      }
    } else if (this._unavailable(option.value)) {
      option.selected = true;
    }

    this._checkCanShowSearchInput();
    setTimeout(() => this._checkUndisplayedSelectedOptions());
  }

  forceFocusItemWhenDownPressed(event: Event) {
    event.preventDefault();
    if (this.filteredOptions.length > 0 && ((this.type === 'single' && this.selectedOptions.length == 0) || this.type != 'single')) {
      const container = (event.target as HTMLElement).parentElement.parentElement.children[1];
      if (container?.children.length > 0) {
        const item = container?.querySelector('.chip-option') as HTMLElement;
        item?.focus();
      }
    }
  }

  @HostListener('window:resize', ['$event'])
  protected onWindowResize(event: Event) {
    if (this.optionsListIsOpened) {
      this._overlayRef?.updateSize({
        width: this.formField?.nativeElement?.clientWidth,
      });
      this._overlayRef?.updatePositionStrategy(this._getOptionsPositionStrategy());
    }
  }

  @HostListener('document:keydown.esc', ['$event'])
  protected onEsc(event: KeyboardEvent) {
    if (this.optionsListIsOpened) {
      this.closeOptions();
    }
  }

  public ngOnDestroy(): void {
    this.closeOptions({ fromOnDestroy: true });
    this._overlayScrollCleanup?.();
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }
}
